import axios from "axios"
import { supabase } from "../supabaseClient"

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL
    ? `${process.env.REACT_APP_SERVER_URL}/api/sms`
    : "/api/sms",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  async (config) => {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the headers
    if (session) {
      config.headers["user-id"] = session.user.id
    }

    // Check if admin has selected a company
    try {
      const selectedCompanyName = localStorage.getItem("selectedCompanyName")
      if (selectedCompanyName) {
        config.headers["x-company-name"] = selectedCompanyName
      }
    } catch {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export type A2PRegistrationStatus =
  | "not_started"
  | "not_required"
  | "submitted"
  | "in_review"
  | "approved"
  | "rejected"
  | "suspended"

export interface SMSPhoneNumber {
  id: number
  phone_number: string
  /** Optional display string from API (e.g. Twilio); normalized in UI when US NANP. */
  phone_number_pretty?: string | null
  phone_number_id?: number
  company_name: string
  country: string
  area_code?: number
  number_type: string
  capabilities: Record<string, boolean>
  twilio_phone_sid: string
  messaging_service_sid?: string
  agent_id?: string
  is_active: boolean
  created_at: string
  updated_at: string
  inbound_config_source: string
  sms_url?: string
  sms_method?: string
  status_callback_url?: string
  a2p_registration_status?: A2PRegistrationStatus
  a2p_campaign_sid?: string
  a2p_campaign_use_case?: string
  a2p_registration_step?: string
  a2p_submitted_at?: string
  a2p_approved_at?: string
  a2p_rejected_at?: string
  a2p_last_status_error?: string
  a2p_registration_error_details?: any
  a2p_brand_sid?: string
  a2p_brand_registration_status?: string
  a2p_customer_profile_sid?: string
  a2p_customer_profile_status?: string
  a2p_campaign_description?: string
  a2p_campaign_message_samples?: string[]
  a2p_campaign_message_flow?: string
  a2p_campaign_has_embedded_links?: boolean
  a2p_campaign_has_embedded_phone?: boolean
  a2p_direct_lending?: boolean
  a2p_age_gated?: boolean
  a2p_campaign_opt_in_keywords?: string
  a2p_campaign_opt_in_message?: string
  a2p_campaign_opt_out_message?: string
  a2p_campaign_help_message?: string
}

export interface AvailablePhoneNumber {
  phoneNumber: string
  friendlyName: string
  locality: string
  region: string
  capabilities: {
    voice?: boolean
    SMS?: boolean
    MMS?: boolean
  }
  isoCountry: string
}

export interface A2PCampaign {
  messaging_service_sid: string
  messaging_service_name: string
  usa2p_sid: string
  campaign_status: string
  use_case: string
  description: string
  message_samples: string[]
  brand_registration_sid: string
  created_at?: string
}

export interface SearchPhoneNumberParams {
  country?: string
  areaCode?: string
  sms?: boolean
  mms?: boolean
  limit?: number
}

export interface BuyPhoneNumberParams {
  phoneNumber?: string
  country?: string
  areaCode?: string
}

export interface SetWebhookParams {
  smsUrl: string
  smsMethod?: "GET" | "POST"
  statusCallbackUrl?: string
}

export interface AssignAgentParams {
  agentId: string | null
}

export interface A2PBusinessInfo {
  business_name: string
  website: string
  registration_number: string
  registration_identifier: "EIN" | "TAX_ID" | "DUNS" | "LEI" | "GIIN"
  business_type: string
  industry: string
  brand_type: "standard" | "low_volume_standard" | "sole_proprietor" | "other"
  address: {
    street: string
    street_secondary?: string
    city: string
    region: string
    postal_code: string
    iso_country: string
  }
  contact: {
    first_name: string
    last_name: string
    email: string
    phone_number: string
    job_position: string
  }
}

export type A2PUseCase =
  | "LOW_VOLUME"
  | "2FA"
  | "ACCOUNT_NOTIFICATION"
  | "CUSTOMER_CARE"
  | "DELIVERY_NOTIFICATION"
  | "FRAUD_ALERT"
  | "HIGHER_EDUCATION"
  | "MARKETING"
  | "MIXED"
  | "POLLING_VOTING"
  | "PUBLIC_SERVICE_ANNOUNCEMENT"
  | "SECURITY_ALERT"
  | "AGENTS_FRANCHISES"
  | "EMERGENCY"
  | "K12_EDUCATION"
  | "PROXY"
  | "SOCIAL"
  | "SWEEPSTAKE"
  | "CHARITY"
  | "POLITICAL"

export interface A2PCampaignInfo {
  brandRegistrationSid: string
  usAppToPersonUsecase: A2PUseCase
  description: string // 40-4096 chars
  messageSamples: string[] // 2-5 samples, 20-1024 chars each
  messageFlow: string // 40-2048 chars
  hasEmbeddedLinks: boolean
  hasEmbeddedPhone: boolean
  directLending: boolean
  ageGated: boolean
  optInKeywords?: string // comma-separated, max 255 chars
  optInMessage?: string // 20-320 chars (required if optInKeywords provided)
  optOutMessage?: string
  helpMessage?: string
}

interface A2PUseCaseMeta {
  value: A2PUseCase
  label: string
  priceMonthly: number
  description: string
  vettingNote?: string
}

// only the value fields changed where needed
export const A2P_USE_CASES: A2PUseCaseMeta[] = [
  {
    value: "LOW_VOLUME",
    label: "Low Volume Mixed",
    priceMonthly: 1.5,
    description:
      "Multi-use (e.g., Customer Care, Delivery Notification) on the lowest throughput tier; recommended for LVS brands or standard brands that don't need high throughput.",
  },
  {
    value: "2FA",
    label: "Two-Factor Authentication (2FA)",
    priceMonthly: 10,
    description: "Any authentication or account verification such as OTP.",
  },
  {
    value: "ACCOUNT_NOTIFICATION",
    label: "Account Notification",
    priceMonthly: 10,
    description:
      "Notifications about the status of an account or related to being a part of an account.",
  },
  {
    value: "CUSTOMER_CARE",
    label: "Customer Care",
    priceMonthly: 10,
    description:
      "Support, account management, and other avenues of customer interaction.",
  },
  {
    value: "DELIVERY_NOTIFICATION",
    label: "Delivery Notification",
    priceMonthly: 10,
    description: "Information about the status of a delivery.",
  },
  {
    value: "FRAUD_ALERT",
    label: "Fraud Alert Messaging",
    priceMonthly: 10,
    description:
      "Messaging about potential fraudulent activity such as spending alerts.",
  },
  {
    value: "HIGHER_EDUCATION",
    label: "Higher Education",
    priceMonthly: 10,
    description:
      "Message campaigns from colleges, universities, and other education institutions.",
  },
  {
    value: "MARKETING",
    label: "Marketing",
    priceMonthly: 10,
    description:
      "Promotional content such as sales and limited-time offers.",
  },
  {
    value: "MIXED",
    label: "Mixed",
    priceMonthly: 10,
    description:
      "Covers multiple use cases such as Customer Care and Delivery Notifications. Likely lower throughput and higher cost per message.",
  },
  {
    value: "POLLING_VOTING",
    label: "Polling and Voting",
    priceMonthly: 10,
    description:
      "For conducting polling and voting, such as customer surveys. Not for political use.",
  },
  {
    value: "PUBLIC_SERVICE_ANNOUNCEMENT",
    label: "Public Service Announcement",
    priceMonthly: 10,
    description: "PSAs to raise audience awareness about a given topic.",
  },
  {
    value: "SECURITY_ALERT",
    label: "Security Alert",
    priceMonthly: 10,
    description:
      "Notification of a compromised system (software or hardware related).",
  },
  {
    value: "AGENTS_FRANCHISES",
    label: "Agents and Franchises",
    priceMonthly: 30,
    description:
      "Brands with multiple agents/franchises/offices needing localized numbers per agent/location/office. Max 5000 numbers/campaign.",
    vettingNote: "Carrier vetting and post-approval required.",
  },
  {
    value: "EMERGENCY",
    label: "Emergency",
    priceMonthly: 5,
    description:
      "Public safety/health notifications during disasters, conflicts, pandemics, and other emergencies.",
  },
  {
    value: "K12_EDUCATION",
    label: "K-12 Education",
    priceMonthly: 10,
    description:
      "For messaging platforms supporting schools (grades K–12) and distance learning centers.",
  },
  {
    value: "PROXY",
    label: "Proxy",
    priceMonthly: 10,
    description:
      "App-based group messaging with proxy/pooled numbers; assigns a number to a conversation and hides personal numbers.",
  },
  {
    value: "SOCIAL",
    label: "Social",
    priceMonthly: 10,
    description:
      "Communication between public figures/influencers and their communities.",
  },
  {
    value: "SWEEPSTAKE",
    label: "Sweepstakes",
    priceMonthly: 10,
    description: "All sweepstakes-related messaging.",
  },
  {
    value: "CHARITY",
    label: "Charity",
    priceMonthly: 10,
    description: "Messaging on behalf of a registered charity or non-profit.",
  },
  {
    value: "POLITICAL",
    label: "Political",
    priceMonthly: 10,
    description:
      "Political messaging campaigns (subject to additional carrier and regulatory requirements).",
  },
]

export const smsPhoneNumberService = {
  // Search available phone numbers
  search: async (params: SearchPhoneNumberParams): Promise<AvailablePhoneNumber[]> => {
    const queryParams = new URLSearchParams()
    if (params.country) queryParams.append("country", params.country)
    if (params.areaCode) queryParams.append("areaCode", params.areaCode)
    if (params.sms !== undefined) queryParams.append("sms", params.sms.toString())
    if (params.mms !== undefined) queryParams.append("mms", params.mms.toString())
    if (params.limit) queryParams.append("limit", params.limit.toString())

    const response = await api.get<AvailablePhoneNumber[]>(
      `/numbers/search?${queryParams.toString()}`,
    )
    return response.data
  },

  // Buy a phone number
  buy: async (params: BuyPhoneNumberParams): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>("/numbers/buy", params)
    return response.data
  },

  // List phone numbers for company
  list: async (): Promise<SMSPhoneNumber[]> => {
    const response = await api.get<SMSPhoneNumber[]>("/numbers")
    return response.data
  },

  // Get specific phone number
  get: async (id: number): Promise<SMSPhoneNumber> => {
    const response = await api.get<SMSPhoneNumber>(`/numbers/${id}`)
    return response.data
  },

  // Set webhook for phone number
  setWebhook: async (
    id: number,
    params: SetWebhookParams,
  ): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>(`/numbers/${id}/webhook`, params)
    return response.data
  },

  // Attach to messaging service
  attachToMessagingService: async (
    phoneNumberDetailId: number,
    createIfMissing = true,
  ): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>("/messaging-services/attach", {
      phoneNumberDetailId,
      createIfMissing,
    })
    return response.data
  },

  // Assign agent to phone number
  assignAgent: async (
    id: number,
    params: AssignAgentParams,
  ): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>(
      `/numbers/${id}/assign-agent`,
      params,
    )
    return response.data
  },

  // Save business info (Step 1)
  saveBusinessInfo: async (
    phoneNumberDetailId: number,
    businessInfo: A2PBusinessInfo,
  ): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>("/a2p/business-info", {
      phoneNumberDetailId,
      businessInfo,
    })
    return response.data
  },

  // Save campaign details (Step 2)
  saveCampaignDetails: async (
    phoneNumberDetailId: number,
    campaignInfo: A2PCampaignInfo,
  ): Promise<SMSPhoneNumber> => {
    const backendCampaignInfo = {
      brand_registration_sid: campaignInfo.brandRegistrationSid,
      use_case: campaignInfo.usAppToPersonUsecase,
      description: campaignInfo.description,
      message_samples: campaignInfo.messageSamples,
      message_flow: campaignInfo.messageFlow,
      has_embedded_links: campaignInfo.hasEmbeddedLinks,
      has_embedded_phone: campaignInfo.hasEmbeddedPhone,
      direct_lending: campaignInfo.directLending,
      age_gated: campaignInfo.ageGated,
      opt_in_keywords: campaignInfo.optInKeywords,
      opt_in_message: campaignInfo.optInMessage,
      opt_out_message: campaignInfo.optOutMessage,
      help_message: campaignInfo.helpMessage,
    }

    const response = await api.post<SMSPhoneNumber>("/a2p/campaign-details", {
      phoneNumberDetailId,
      campaignInfo: backendCampaignInfo,
    })
    return response.data
  },

  // Submit A2P registration (Step 3)
  submitA2P: async (phoneNumberDetailId: number): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>("/a2p/submit", {
      phoneNumberDetailId,
    })
    return response.data
  },

  // Get A2P registration status
  getA2PStatus: async (id: number): Promise<any> => {
    const response = await api.get<any>(`/a2p/status/${id}`)
    return response.data
  },

  // Sync A2P registration status from Twilio
  syncA2PStatus: async (id: number): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>(`/a2p/sync-status/${id}`)
    return response.data
  },

  // Delete phone number
  delete: async (id: number): Promise<void> => {
    await api.delete(`/numbers/${id}`)
  },

  // List campaigns (backend will already scope to this company + typically VERIFIED)
  listA2PCampaigns: async (): Promise<A2PCampaign[]> => {
    const response = await api.get<A2PCampaign[]>("/a2p/campaigns")
    return response.data
  },

  connectToCampaign: async (
    id: number,
    params: {
      messagingServiceSid: string
      campaignSid: string
      campaignUseCase?: string
    },
  ): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>(
      `/numbers/${id}/connect-campaign`,
      params,
    )
    return response.data
  },

  // Disconnect from campaign
  disconnectFromCampaign: async (id: number): Promise<SMSPhoneNumber> => {
    const response = await api.post<SMSPhoneNumber>(
      `/numbers/${id}/disconnect-campaign`,
    )
    return response.data
  },

  // Sync campaigns directly from Twilio (no DB table required)
  syncA2PCampaigns: async (): Promise<{
    message: string
    campaigns: A2PCampaign[]
  }> => {
    const response = await api.post<{ message: string; campaigns: A2PCampaign[] }>(
      "/a2p/sync-campaigns",
    )
    return response.data
  },
}
